import React, {useContext} from 'react';

import clsx from 'clsx';
import {formatLicenseDate} from 'src/modules/profile/utils/formatLicenseDate';
import {ProductSubscriptionCapacityStatus} from 'src/openapi/admin/models/ProductSubscriptionCapacityStatus';
import {ProductSubscriptionLifecycleStatus} from 'src/openapi/admin/models/ProductSubscriptionLifecycleStatus';

import {Dropdown} from '@pb/kit-component-dropdown';
import {Group, Item} from '@pb/kit-component-group';
import {Icon, Icons} from '@pb/kit-component-icon';
import {ProgressBar} from '@pb/kit-component-stepper';
import {LocaleContext} from '@pb/kit-context-locale';
import {colors} from '@pb/kit-stylus/config.json';

import {getSubscriptionSeatText, isUnlimitedSubscription} from './subscription-seat-text';

import {
    sectionCapacityWarning,
    sectionDateRange,
    sectionProductName,
    sectionProgressWrapper,
    sectionStatusDot,
    sectionStatusDotActive,
    sectionStatusDotScheduled,
    sectionStatusLabel,
    sectionStatusLabelActive,
    sectionStatusLabelScheduled,
    sectionSubscriptionRow,
} from './product-access-summary-section.module.styl';

import type {ProductSubscription} from 'src/openapi/admin/models/ProductSubscription';

const STATUS_CONFIG = {
    [ProductSubscriptionLifecycleStatus.ACTIVE]: {
        label: 'Active',
        dotClass: sectionStatusDotActive,
        labelClass: sectionStatusLabelActive,
    },
    [ProductSubscriptionLifecycleStatus.PENDING]: {
        label: 'Pending',
        dotClass: sectionStatusDotScheduled,
        labelClass: sectionStatusLabelScheduled,
    },
} as const;

type Props = {
    subscription: ProductSubscription,
};

export const SubscriptionRow: React.FC<Props> = ({subscription}) => {
    const {locale} = useContext(LocaleContext);

    const statusConfig = STATUS_CONFIG[subscription.lifecycleStatus as keyof typeof STATUS_CONFIG];
    const isOverCapacity = subscription.capacityStatus === ProductSubscriptionCapacityStatus.OVER_CAPACITY;
    const isUnlimited = isUnlimitedSubscription(subscription);
    const percentUsed = isUnlimited
        ? 0
        : Math.min((subscription.activeSeatCount / subscription.seats) * 100, 100);

    const seatText = getSubscriptionSeatText(subscription);

    const formattedStart = formatLicenseDate(locale.dateFormat, subscription.startDate);
    const formattedEnd = formatLicenseDate(locale.dateFormat, subscription.endDate);

    return (
        <div className={sectionSubscriptionRow}>
            <Group direction="v" gap="xxs">
                <Group gap="s" alignItems="center">
                    {statusConfig ? (
                        <>
                            <span className={clsx(sectionStatusDot, statusConfig.dotClass)}/>
                            <span className={clsx(sectionStatusLabel, statusConfig.labelClass)}>
                                {statusConfig.label}
                            </span>
                        </>
                    ) : null}
                    <span className={sectionProductName}>
                        {subscription.product.name}
                    </span>
                </Group>
                <Group gap="xxs" alignItems="center">
                    <div className={sectionDateRange}>
                        {formattedStart} → {formattedEnd}
                    </div>
                    {!isUnlimited ? (
                        <Item className={sectionProgressWrapper}>
                            <ProgressBar
                                progress={percentUsed}
                                fullColor={colors.primaryColors.skyBlueDarkest}
                                emptyColor={colors.primaryColors.gray}
                            />
                        </Item>
                    ) : null}
                    <span>{seatText}</span>
                    {isOverCapacity ? (
                        <Dropdown
                            hoverEnabled={true}
                            role="tooltip"
                            anchor={({anchorRef, getAnchorProps}) => (
                                <span
                                    ref={anchorRef}
                                    {...getAnchorProps()}
                                    aria-label="Subscription requires manual involvement">
                                    <Icon type={Icons.WARNING} className={sectionCapacityWarning}/>
                                </span>
                            )}>
                            <span>Seats over capacity. Manual involvement needed</span>
                        </Dropdown>
                    ) : null}
                </Group>
            </Group>
        </div>
    );
};
